<template>
  <div class="quick-replies-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="quick-replies-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1 class="page-title">
                <span class="page-icon">⚡</span>
                Quick Replies
              </h1>
              <p class="page-description">Create and manage quick reply templates for faster messaging</p>
            </div>
            <button @click="openCreateModal" class="btn-primary" :disabled="quickReplies.length >= maxAllowed">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Add Quick Reply
            </button>
          </div>

          <!-- Limit Info Banner -->
          <div class="limit-banner" :class="{ 'limit-reached': quickReplies.length >= maxAllowed }">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <span>
              {{ quickReplies.length }} / {{ maxAllowed }} quick replies used
              <span v-if="quickReplies.length >= maxAllowed" class="limit-warning">
                (Maximum limit reached)
              </span>
            </span>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading quick replies...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="quickReplies.length === 0" class="empty-state">
            <div class="empty-icon">⚡</div>
            <h3>No Quick Replies Yet</h3>
            <p>Create quick reply templates to respond faster to common questions and messages</p>
            <button @click="openCreateModal" class="btn-primary btn-large">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Create Your First Quick Reply
            </button>
          </div>

          <!-- Quick Replies Grid -->
          <div v-else class="quick-replies-grid">
            <div v-for="reply in quickReplies" :key="reply.id" class="quick-reply-card">
              <div class="card-header">
                <h3 class="card-title">{{ reply.title }}</h3>
                <div class="card-actions">
                  <button @click="openEditModal(reply)" class="action-btn edit-btn" title="Edit">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                  </button>
                  <button @click="confirmDelete(reply)" class="action-btn delete-btn" title="Delete">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="card-message">
                <p>{{ reply.message }}</p>
              </div>
              <div class="card-footer">
                <span class="card-date">Created {{ formatDate(reply.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h2>{{ editMode ? 'Edit' : 'Create' }} Quick Reply</h2>
          <button @click="closeModal" class="modal-close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label for="title" class="form-label">
              <span class="required">*</span> Title
            </label>
            <input
              id="title"
              v-model="formData.title"
              type="text"
              class="form-input"
              placeholder="e.g., Greeting, Business Hours, Pricing Info"
              maxlength="100"
              @input="clearError('title')"
            />
            <p class="form-help">{{ formData.title.length }} / 100 characters</p>
            <p v-if="errors.title" class="form-error">{{ errors.title }}</p>
          </div>

          <div class="form-group">
            <label for="message" class="form-label">
              <span class="required">*</span> Message
            </label>
            <textarea
              id="message"
              v-model="formData.message"
              class="form-textarea"
              rows="6"
              placeholder="Enter your quick reply message..."
              @input="clearError('message')"
            ></textarea>
            <p class="form-help">This message will be sent when you select this quick reply</p>
            <p v-if="errors.message" class="form-error">{{ errors.message }}</p>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn-secondary">Cancel</button>
          <button @click="saveQuickReply" class="btn-primary" :disabled="saving">
            <div v-if="saving" class="saving-spinner"></div>
            <span v-else>{{ editMode ? 'Update' : 'Create' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
      <div class="modal confirm-modal" @click.stop>
        <div class="modal-header">
          <h2>Delete Quick Reply</h2>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete "<strong>{{ deleteTarget?.title }}</strong>"?</p>
          <p class="warning-text">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button @click="showDeleteModal = false" class="btn-secondary">Cancel</button>
          <button @click="deleteQuickReply" class="btn-danger" :disabled="deleting">
            <div v-if="deleting" class="saving-spinner"></div>
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Notification -->
    <div v-if="notification.show" class="notification" :class="notification.type">
      <svg v-if="notification.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
      </svg>
      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
      </svg>
      <span>{{ notification.message }}</span>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { DashboardService } from '@/services/dashboard'

export default {
  name: 'QuickRepliesView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      loading: true,
      quickReplies: [],
      maxAllowed: 5,
      showModal: false,
      editMode: false,
      saving: false,
      formData: {
        title: '',
        message: ''
      },
      errors: {},
      currentEditId: null,
      showDeleteModal: false,
      deleteTarget: null,
      deleting: false,
      notification: {
        show: false,
        type: 'success',
        message: ''
      }
    }
  },
  async mounted () {
    await this.loadQuickReplies()
  },
  methods: {
    async loadQuickReplies () {
      console.log('[QuickReplies] loadQuickReplies called')
      try {
        this.loading = true
        console.log('[QuickReplies] Calling DashboardService.getQuickReplies()')
        const result = await DashboardService.getQuickReplies()
        console.log('[QuickReplies] API result:', result)

        if (result.success) {
          this.quickReplies = result.quick_replies || []
          this.maxAllowed = result.max_allowed || 5
          console.log('[QuickReplies] Loaded', this.quickReplies.length, 'quick replies')
        } else {
          console.error('[QuickReplies] Failed:', result.error)
          this.showNotification('error', 'Failed to load quick replies')
        }
      } catch (error) {
        console.error('[QuickReplies] Error loading quick replies:', error)
        this.showNotification('error', 'Error loading quick replies')
      } finally {
        this.loading = false
      }
    },

    openCreateModal () {
      if (this.quickReplies.length >= this.maxAllowed) {
        this.showNotification('error', `Maximum limit of ${this.maxAllowed} quick replies reached`)
        return
      }

      this.editMode = false
      this.formData = { title: '', message: '' }
      this.errors = {}
      this.showModal = true
    },

    openEditModal (reply) {
      this.editMode = true
      this.currentEditId = reply.id
      this.formData = {
        title: reply.title,
        message: reply.message
      }
      this.errors = {}
      this.showModal = true
    },

    closeModal () {
      this.showModal = false
      this.formData = { title: '', message: '' }
      this.errors = {}
      this.currentEditId = null
    },

    validateForm () {
      this.errors = {}

      if (!this.formData.title.trim()) {
        this.errors.title = 'Title is required'
      }

      if (!this.formData.message.trim()) {
        this.errors.message = 'Message is required'
      }

      return Object.keys(this.errors).length === 0
    },

    clearError (field) {
      if (this.errors[field]) {
        delete this.errors[field]
      }
    },

    async saveQuickReply () {
      console.log('saveQuickReply called', { editMode: this.editMode, formData: this.formData })

      if (!this.validateForm()) {
        console.log('Validation failed', this.errors)
        return
      }

      try {
        this.saving = true
        console.log('Calling API...', this.editMode ? 'UPDATE' : 'CREATE')

        let result
        if (this.editMode) {
          result = await DashboardService.updateQuickReply(this.currentEditId, this.formData)
        } else {
          result = await DashboardService.createQuickReply(this.formData)
        }

        console.log('API response:', result)

        if (result.success) {
          this.showNotification('success', `Quick reply ${this.editMode ? 'updated' : 'created'} successfully`)
          this.closeModal()
          await this.loadQuickReplies()
        } else {
          this.showNotification('error', result.message || 'Failed to save quick reply')
        }
      } catch (error) {
        console.error('Error saving quick reply:', error)
        this.showNotification('error', 'Error saving quick reply: ' + error.message)
      } finally {
        this.saving = false
      }
    },

    confirmDelete (reply) {
      this.deleteTarget = reply
      this.showDeleteModal = true
    },

    async deleteQuickReply () {
      try {
        this.deleting = true
        const result = await DashboardService.deleteQuickReply(this.deleteTarget.id)

        if (result.success) {
          this.showNotification('success', 'Quick reply deleted successfully')
          this.showDeleteModal = false
          this.deleteTarget = null
          await this.loadQuickReplies()
        } else {
          this.showNotification('error', result.message || 'Failed to delete quick reply')
        }
      } catch (error) {
        console.error('Error deleting quick reply:', error)
        this.showNotification('error', 'Error deleting quick reply')
      } finally {
        this.deleting = false
      }
    },

    showNotification (type, message) {
      this.notification = { show: true, type, message }
      setTimeout(() => {
        this.notification.show = false
      }, 4000)
    },

    formatDate (dateString) {
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays} days ago`
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
      return date.toLocaleDateString()
    }
  }
}
</script>

<style scoped>
/* Layout */
.quick-replies-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  padding: 32px;
}

.quick-replies-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;
}

.header-content {
  flex: 1;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
  transition: color 0.3s ease;
}

.page-icon {
  font-size: 32px;
}

.page-description {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

/* Limit Banner */
.limit-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border-radius: 12px;
  margin-bottom: 24px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
}

.limit-banner.limit-reached {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.limit-warning {
  font-weight: 600;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-primary);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 24px;
  opacity: 0.3;
}

.empty-state h3 {
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 12px;
}

.empty-state p {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0 0 32px;
  max-width: 500px;
}

/* Quick Replies Grid */
.quick-replies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.quick-reply-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.quick-reply-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-actions {
  display: flex;
  gap: 6px;
}

.action-btn {
  padding: 6px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background: var(--bg-tertiary);
}

.edit-btn:hover {
  color: #3b82f6;
}

.delete-btn:hover {
  color: #ef4444;
}

.card-message {
  flex: 1;
}

.card-message p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  padding-top: 12px;
  border-top: 1px solid var(--border-primary);
}

.card-date {
  font-size: 12px;
  color: var(--text-tertiary);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.confirm-modal {
  max-width: 450px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close-btn {
  padding: 4px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--border-primary);
}

/* Form */
.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.required {
  color: #ef4444;
  margin-right: 4px;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-secondary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.form-help {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 6px 0 0;
}

.form-error {
  font-size: 12px;
  color: #ef4444;
  margin: 6px 0 0;
}

.warning-text {
  color: #f59e0b;
  font-weight: 500;
  margin-top: 8px;
}

/* Buttons */
.btn-primary,
.btn-secondary,
.btn-danger {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-large {
  padding: 14px 28px;
  font-size: 16px;
}

.btn-secondary {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-secondary);
}

.btn-secondary:hover {
  background: var(--bg-secondary);
  border-color: var(--border-primary);
}

.btn-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.saving-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* Notification */
.notification {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 16px 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  animation: slideIn 0.3s ease-out;
}

.notification.success {
  background: #10b981;
  color: white;
}

.notification.error {
  background: #ef4444;
  color: white;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes slideIn {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .page-title {
    font-size: 24px;
  }

  .quick-replies-grid {
    grid-template-columns: 1fr;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }

  .notification {
    left: 16px;
    right: 16px;
    bottom: 16px;
  }

  .modal {
    margin: 0 16px;
  }
}
</style>
